import { MoreHorizontal, Settings2, Users } from 'lucide-react';
import { useState } from 'react';

import { useAdvisorsAdminControllerListar } from '@/api/generated/admin-advisors/admin-advisors';
import type { AdvisorAdminResponse } from '@/api/generated/model';
import { AdvisorConfiguracaoDialogo } from '@/features/admin/advisors/components/AdvisorConfiguracaoDialogo';
import { ROTULO_DIA_ABREVIADO } from '@/shared/lib/diasDaSemana';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Skeleton } from '@/shared/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

/**
 * "Advisor Personalizado" — disponibilidade (dias da semana) e capacidade
 * semanal por advisor. Só afeta a ENTRADA de discussões novas
 * (`GET /discussoes/advisors-disponiveis`); discussões já abertas não mudam.
 */
export function AdvisorsPage() {
  const { pode } = usePermissoes();
  const podeEditar = pode('admin_advisors.editar');

  const [configurando, setConfigurando] = useState<AdvisorAdminResponse | null>(null);

  const { data, isPending, isError, refetch } = useAdvisorsAdminControllerListar();

  return (
    <div className="space-y-6">
      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10">
          <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">Advisors</h3>
        </div>

        {isPending ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 rounded-md" />
            ))}
          </div>
        ) : isError ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : data.itens.length === 0 ? (
          <EstadoVazio
            icone={Users}
            titulo="Nenhum advisor cadastrado"
            descricao="Advisors aparecem aqui assim que um usuário recebe o cargo de assessor."
          />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Saldo atual</TableHead>
                  <TableHead>Capacidade</TableHead>
                  <TableHead>Dias disponíveis</TableHead>
                  <TableHead>Próximo dia disponível</TableHead>
                  <TableHead className="w-20">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.itens.map((advisor) => (
                  <TableRow key={advisor.id}>
                    <TableCell className="text-foreground">{advisor.nome}</TableCell>
                    <TableCell>
                      {advisor.ativo ? (
                        <Badge variant="outline" className="text-emerald-600 border-emerald-600/40">
                          Ativo
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-destructive border-destructive/40">
                          Inativo
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-foreground">{advisor.saldoAtual}</TableCell>
                    <TableCell className="text-muted-foreground">{advisor.capacidadeSemanal}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {advisor.diasDisponiveis.length === 0
                        ? '—'
                        : advisor.diasDisponiveis.map((d) => ROTULO_DIA_ABREVIADO[d]).join(', ')}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {advisor.proximoDiaDisponivel ? ROTULO_DIA_ABREVIADO[advisor.proximoDiaDisponivel] : '—'}
                    </TableCell>
                    <TableCell>
                      {podeEditar && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" aria-label={`Ações de ${advisor.nome}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setConfigurando(advisor)}>
                              <Settings2 className="w-4 h-4" />
                              Configurar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {configurando && (
        <AdvisorConfiguracaoDialogo
          aberto
          aoFechar={() => setConfigurando(null)}
          advisor={configurando}
        />
      )}
    </div>
  );
}
